import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Modal, 
  Form, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Avatar,
  Statistic,
  DatePicker,
  Divider,
  Switch,
  Checkbox,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CloseOutlined,
  SaveOutlined,
  LoadingOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserAddOutlined,
  LockOutlined,
  UnlockOutlined,
  CrownOutlined,
  SettingOutlined,
  SafetyOutlined,
  KeyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { AdminPermission as GraphQLAdminPermission, AdminRole as GraphQLAdminRole } from '../generated/graphql';
import { useTranslation } from '../hooks/useTranslation';
import { getSpecializationLabel } from '../constants/specializations';
import { useMutation, useQuery } from '@apollo/client';
import { 
  CREATE_ADMIN_USER, 
  UPDATE_ADMIN_USER, 
  DELETE_ADMIN_USER, 
  ACTIVATE_ADMIN_USER, 
  DEACTIVATE_ADMIN_USER,
  GET_ADMIN_USERS,
  GET_ADMIN_USER_ROLES,
  GET_ADMIN_USER_ROLE_PERMISSIONS,
  UPDATE_ADMIN_USER_ROLE,
  CREATE_ADMIN_USER_ROLE,
  RESET_ADMIN_USER_PASSWORD
} from '../graphql/mutations';
import { GET_DOCTORS, GET_CLINICS } from '../graphql/queries';
import { 
  AdminUsersTab, 
  AdminRolesTab, 
  AdminUserModal, 
  AdminUserPasswordResetModal,
  RoleModal,
  AdminUser,
  AdminRole,
  AdminPermission as AdminPermissionType,
  PERMISSION_METADATA,
} from '../components/AdminUsers';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const AdminUsers: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const { canManageAdmins, canCreateUsers, canUpdateUsers, canDeleteUsers } = usePermissions();
  
  // GraphQL Queries and Mutations
  const { data: adminUsersData, loading: loadingAdminUsers, error: adminUsersError, refetch } = useQuery(GET_ADMIN_USERS);
  const { data: doctorsData, loading: loadingDoctors } = useQuery(GET_DOCTORS);
  const { data: clinicsData, loading: loadingClinics } = useQuery(GET_CLINICS);
  const { data: adminRolesData, loading: loadingAdminRoles, error: adminRolesError, refetch: refetchRoles } = useQuery(GET_ADMIN_USER_ROLES);
  const { data: adminRolePermissionsData, loading: loadingRolePermissions } = useQuery(GET_ADMIN_USER_ROLE_PERMISSIONS);
  const [createAdminUser] = useMutation(CREATE_ADMIN_USER);
  const [updateAdminUser] = useMutation(UPDATE_ADMIN_USER);
  const [deleteAdminUser] = useMutation(DELETE_ADMIN_USER);
  const [activateAdminUser] = useMutation(ACTIVATE_ADMIN_USER);
  const [deactivateAdminUser] = useMutation(DEACTIVATE_ADMIN_USER);
  const [resetAdminUserPassword] = useMutation(RESET_ADMIN_USER_PASSWORD);
  const [updateAdminUserRole] = useMutation(UPDATE_ADMIN_USER_ROLE);
  const [createAdminUserRole] = useMutation(CREATE_ADMIN_USER_ROLE);
  
  const adminUsers = adminUsersData?.adminUsers || [];
  const doctors = doctorsData?.doctors || [];
  const clinics = clinicsData?.clinics || [];

  // Tab state
  const [activeTab, setActiveTab] = useState('adminUsers');
  
  // Admin Users tab state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [editingAdminPermissions, setEditingAdminPermissions] = useState<AdminUser | null>(null);
  const [editingAdminRole, setEditingAdminRole] = useState<AdminUser | null>(null);
  const [resettingPasswordAdmin, setResettingPasswordAdmin] = useState<AdminUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [permissionsForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  // Admin Roles tab state
  const [editingRole, setEditingRole] = useState<any>(null);
  const [editingRolePermissions, setEditingRolePermissions] = useState<any>(null);
  const [adminRoleForm] = Form.useForm();
  const [rolePermissionsForm] = Form.useForm();

  // Real data from GraphQL queries
  const availableRoles = (adminRolesData?.adminUserRoles || []).map((role: any) => ({
    ...role,
    user_count: adminUsers.filter((u: AdminUser) => u.role_id === role.id).length
  }));

  // Fallback roles if the query fails or returns no data
  const fallbackRoles = [
    { id: 'role-super-admin', name: 'Super Administrator', description: 'Full system access', is_active: true },
    { id: 'role-admin', name: 'Administrator', description: 'Most system permissions', is_active: true },
    { id: 'role-manager', name: 'Manager', description: 'Limited permissions for operational management', is_active: true },
    { id: 'role-operator', name: 'Operator', description: 'Read-only access with appointment management', is_active: true },
    { id: 'role-nurse', name: 'Nurse', description: 'Nursing staff permissions', is_active: true },
    { id: 'role-doctor', name: 'Doctor', description: 'Doctor-specific permissions', is_active: true }
  ];

  // Use fallback roles if no roles are loaded and not currently loading
  const finalAvailableRoles = availableRoles.length > 0 ? availableRoles : 
    (!loadingAdminRoles && !adminRolesError) ? fallbackRoles : availableRoles;

  // Debug: Log roles data
  console.log('Admin roles data:', adminRolesData);
  console.log('Available roles:', availableRoles);
  console.log('Final available roles:', finalAvailableRoles);
  console.log('Loading roles:', loadingAdminRoles);
  console.log('Admin roles error:', adminRolesError);

  // Transform the permission data to include metadata
  const availablePermissions = (adminRolePermissionsData?.adminUserRolePermissions || [])
    .map((perm: any) => {
      const metadata = PERMISSION_METADATA[perm.permission];
      return {
        id: perm.permission, // Use permission as ID since there's no separate ID
        permission: perm.permission,
        name: metadata?.name || perm.permission,
        description: metadata?.description || perm.permission,
        category: metadata?.category || 'other',
        is_active: true
      };
    });

  const filteredAdminUsers = adminUsers.filter((admin: AdminUser) => {
    const matchesSearch = 
      admin.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || admin.role_id === roleFilter;
    const matchesStatus = statusFilter === 'all' || admin.is_active === (statusFilter === 'active');
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleCreateAdmin = async (values: any) => {
    setIsSubmitting(true);
    try {
      const { data } = await createAdminUser({
        variables: {
          createAdminUserDto: {
            email: values.email,
            firstname: values.firstname,
            lastname: values.lastname,
            role_id: values.role_id, // Use role_id to link to AdminUserRole
            phone_number: values.phone_number,
            password: values.password,
            doctor_id: values.doctor_id || null, // Explicitly handle null/undefined values
            clinic_id: values.clinic_id || null // Explicitly handle null/undefined values
          }
        }
      });
      
      if (data?.createAdminUser) {
        setShowCreateModal(false);
        form.resetFields();
        message.success(t('adminUsers.adminUserCreated'));
        refetch(); // Refresh the admin users list
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      message.error(t('adminUsers.errorCreatingAdminUser'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (id: string, values: any) => {
    setIsSubmitting(true);
    try {
      const { data } = await updateAdminUser({
        variables: {
          id,
          updateAdminUserDto: {
            firstname: values.firstname,
            lastname: values.lastname,
            role_id: values.role_id, // Use role_id to link to AdminUserRole
            phone_number: values.phone_number,
            is_active: values.is_active,
            doctor_id: values.doctor_id || null, // Explicitly handle null/undefined values
            clinic_id: values.clinic_id || null // Explicitly handle null/undefined values
          }
        }
      });
      
      if (data?.updateAdminUser) {
        setEditingAdmin(null);
        form.resetFields();
        message.success(t('adminUsers.adminUserUpdated'));
        refetch(); // Refresh the admin users list
      }
    } catch (error) {
      console.error('Error updating admin user:', error);
      message.error(t('adminUsers.errorUpdatingAdminUser'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdminPermissions = async (id: string, values: any) => {
    // Note: Individual user permissions are not supported in the backend.
    // Permissions are managed through roles. This function is kept for UI compatibility
    // but should redirect users to manage permissions through roles instead.
    message.warning(t('adminUsers.permissionsManagedThroughRoles'));
    setEditingAdminPermissions(null);
    permissionsForm.resetFields();
  };

  const handleUpdateAdminRole = async (id: string, values: any) => {
    setIsSubmitting(true);
    try {
      const { data } = await updateAdminUser({
        variables: {
          id,
          updateAdminUserDto: {
            role_id: values.role_id
          }
        }
      });
      
      if (data?.updateAdminUser) {
        setEditingAdminRole(null);
        roleForm.resetFields();
        message.success(t('adminUsers.adminUserRoleUpdated'));
        refetch(); // Refresh the admin users list
      }
    } catch (error) {
      console.error('Error updating admin role:', error);
      message.error(t('adminUsers.errorUpdatingAdminRole'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRolePermissions = async (roleId: string, values: any) => {
    setIsSubmitting(true);
    try {
      // Since we're using permission enum values directly, we can pass them as permission_ids
      const selectedPermissionIds = values.permissions || [];

      await updateAdminUserRole({
        variables: {
          id: roleId,
          updateAdminUserRoleDto: {
            permission_ids: selectedPermissionIds
          }
        }
      });
      
      setEditingRolePermissions(null);
      rolePermissionsForm.resetFields();
      message.success(t('adminUsers.rolePermissionsUpdated'));
      
      // Refetch the roles data to get updated permissions
      refetchRoles();
    } catch (error) {
      console.error('Error updating role permissions:', error);
      message.error(t('adminUsers.errorUpdatingRolePermissions'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateRole = async (values: any) => {
    setIsSubmitting(true);
    try {
      const { data } = await createAdminUserRole({
        variables: {
          createAdminUserRoleDto: {
            name: values.name,
            description: values.description,
            is_active: values.is_active !== false,
            permission_ids: values.permission_ids || []
          }
        }
      });
      
      if (data?.createAdminUserRole) {
        setEditingRole(null);
        adminRoleForm.resetFields();
        message.success(t('adminUsers.roleCreated'));
        refetchRoles();
      }
    } catch (error) {
      console.error('Error creating role:', error);
      message.error(t('adminUsers.errorCreatingRole'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRole = async (id: string, values: any) => {
    setIsSubmitting(true);
    try {
      const { data } = await updateAdminUserRole({
        variables: {
          id,
          updateAdminUserRoleDto: {
            name: values.name,
            description: values.description,
            is_active: values.is_active !== false,
            permission_ids: values.permission_ids || []
          }
        }
      });
      
      if (data?.updateAdminUserRole) {
        setEditingRole(null);
        adminRoleForm.resetFields();
        message.success(t('adminUsers.roleUpdated'));
        refetchRoles();
      }
    } catch (error) {
      console.error('Error updating role:', error);
      message.error(t('adminUsers.errorUpdatingRole'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (id: string) => {
    try {
      const { data } = await deleteAdminUser({
        variables: { id }
      });
      
      if (data?.deleteAdminUser) {
        message.success(t('adminUsers.adminUserDeleted'));
        refetch(); // Refresh the admin users list
      }
    } catch (error) {
      console.error('Error deleting admin user:', error);
      message.error(t('adminUsers.errorDeletingAdminUser'));
    }
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      const { data } = await (isActive ? activateAdminUser : deactivateAdminUser)({
        variables: { id }
      });
      
      if (data) {
        message.success(t('adminUsers.adminUserStatusUpdated', { status: isActive ? t('adminUsers.activated') : t('adminUsers.deactivated') }));
        refetch(); // Refresh the admin users list
      }
    } catch (error) {
      console.error('Error updating admin status:', error);
      message.error(t('adminUsers.errorUpdatingAdminStatus'));
    }
  };

  const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    if (!resettingPasswordAdmin) return;
    
    try {
      setIsSubmitting(true);
      const { data } = await resetAdminUserPassword({
        variables: { 
          id: resettingPasswordAdmin.id, 
          newPassword: values.newPassword 
        }
      });
      
      if (data?.resetAdminUserPassword?.success) {
        message.success(t('adminUsers.passwordResetSuccess'));
        setResettingPasswordAdmin(null);
        form.resetFields();
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      message.error(t('adminUsers.passwordResetError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleTag = (role: string) => {
    const roleConfig = {
      super_admin: { color: 'red', icon: <CrownOutlined /> },
      admin: { color: 'blue', icon: <SettingOutlined /> },
      manager: { color: 'green', icon: <SafetyOutlined /> },
      operator: { color: 'orange', icon: <UserOutlined /> },
      doctor: { color: 'purple', icon: <UserOutlined /> },
      nurse: { color: 'cyan', icon: <UserOutlined /> }
    };
    const config = roleConfig[role as keyof typeof roleConfig] || { color: 'default', icon: <UserOutlined /> };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {t(`adminUsers.roles.${role.toUpperCase()}`)}
      </Tag>
    );
  };

  const getStatusTag = (isActive: boolean) => {
    return isActive ? (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        {t('adminUsers.statuses.active')}
      </Tag>
    ) : (
      <Tag color="warning" icon={<ExclamationCircleOutlined />}>
        {t('adminUsers.statuses.inactive')}
      </Tag>
    );
  };


  const columns = [
    {
      title: t('adminUsers.adminUser'),
      key: 'admin',
      render: (admin: AdminUser) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40}
            style={{ backgroundColor: admin.is_super_admin ? '#f5222d' : '#1890ff' }}
            icon={admin.is_super_admin ? <CrownOutlined /> : <UserOutlined />}
          />
          <div style={{ marginLeft: 12 }}>
            <div style={{ fontWeight: 500 }}>
              {admin.firstname} {admin.lastname}
              {admin.is_super_admin && (
                <CrownOutlined style={{ marginLeft: 4, color: '#f5222d' }} />
              )}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('adminUsers.id')}: {admin.id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('adminUsers.contact'),
      key: 'contact',
      render: (admin: AdminUser) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{admin.email}</Text>
          </div>
          {admin.phone_number && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
              <Text style={{ fontSize: '12px' }}>{admin.phone_number}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('adminUsers.role'),
      key: 'role',
      render: (admin: AdminUser) => {
        // Get role name from userRole relationship or fallback to role field
        const roleName = admin.userRole?.name || "-";
        return getRoleTag(roleName);
      },
    },
    {
      title: t('adminUsers.linkedDoctor'),
      key: 'doctor',
      render: (admin: AdminUser) => {
        if (admin.doctor) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                {admin.doctor.first_name_en} {admin.doctor.last_name_en}
              </Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {admin.doctor.specialization}
              </Text>
            </div>
          );
        }
        return <Text type="secondary" style={{ fontSize: '12px' }}>{t('adminUsers.noDoctor')}</Text>;
      },
    },
    {
      title: t('adminUsers.linkedClinic'),
      key: 'clinic',
      render: (admin: AdminUser) => {
        if (admin.clinic) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                {admin.clinic.internal_name || admin.clinic.name_zh_hk}
              </Text>
              {admin.clinic.district && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {admin.clinic.district}
                </Text>
              )}
            </div>
          );
        }
        return <Text type="secondary" style={{ fontSize: '12px' }}>{t('adminUsers.noClinic')}</Text>;
      },
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (admin: AdminUser) => getStatusTag(admin.is_active),
    },
    {
      title: t('adminUsers.lastLogin'),
      key: 'lastLogin',
      render: (admin: AdminUser) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
          <Text style={{ fontSize: '12px' }}>
            {admin.last_login_at 
              ? new Date(admin.last_login_at).toLocaleDateString()
              : t('adminUsers.never')
            }
          </Text>
        </div>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (admin: AdminUser) => (
        <Space>
          <PermissionGuard permission={GraphQLAdminPermission.UpdateUsers}>
            {/* <Tooltip title={admin.is_active ? t('adminUsers.deactivate') : t('adminUsers.activate')}>
              <Switch
                checked={admin.is_active}
                onChange={(checked) => handleToggleStatus(admin.id, checked)}
                size="small"
              />
            </Tooltip> */}
            <Tooltip title={t('adminUsers.editBasicInfo')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingAdmin(admin);
                  form.resetFields();
                  form.setFieldsValue({
                    ...admin,
                    role_id: admin.role_id, // Use role_id to link to AdminUserRole
                    created_at: admin.created_at ? dayjs(admin.created_at) : null
                  });
                }}
              />
            </Tooltip>
            <Tooltip title={t('adminUsers.resetPassword')}>
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => setResettingPasswordAdmin(admin)}
              />
            </Tooltip>
          </PermissionGuard>
          <PermissionGuard permission={GraphQLAdminPermission.DeleteUsers}>
            <Popconfirm
              title={t('adminUsers.deleteConfirm')}
              onConfirm={() => handleDeleteAdmin(admin.id)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Tooltip title={t('common.delete')}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={admin.is_super_admin}
                />
              </Tooltip>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  const activeAdmins = adminUsers.filter((a: AdminUser) => a.is_active).length;
  const inactiveAdmins = adminUsers.filter((a: AdminUser) => !a.is_active).length;
  const superAdmins = adminUsers.filter((a: AdminUser) => a.is_super_admin).length;

  const tabItems = [
    {
      key: 'adminUsers',
      label: t('adminUsers.adminUsers'),
      children: <AdminUsersTab 
        adminUsers={adminUsers}
        filteredAdminUsers={filteredAdminUsers}
        loadingAdminUsers={loadingAdminUsers}
        adminUsersError={adminUsersError}
        refetch={refetch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        editingAdmin={editingAdmin}
        setEditingAdmin={setEditingAdmin}
        editingAdminPermissions={editingAdminPermissions}
        setEditingAdminPermissions={setEditingAdminPermissions}
        editingAdminRole={editingAdminRole}
        setEditingAdminRole={setEditingAdminRole}
        isSubmitting={isSubmitting}
        form={form}
        permissionsForm={permissionsForm}
        roleForm={roleForm}
        doctors={doctors}
        loadingDoctors={loadingDoctors}
        availableRoles={finalAvailableRoles}
        handleCreateAdmin={handleCreateAdmin}
        handleUpdateAdmin={handleUpdateAdmin}
        handleUpdateAdminPermissions={handleUpdateAdminPermissions}
        handleUpdateAdminRole={handleUpdateAdminRole}
        handleDeleteAdmin={handleDeleteAdmin}
        handleToggleStatus={handleToggleStatus}
        getRoleTag={getRoleTag}
        getStatusTag={getStatusTag}
        columns={columns}
        activeAdmins={activeAdmins}
        inactiveAdmins={inactiveAdmins}
        superAdmins={superAdmins}
      />
    },
    {
      key: 'adminRoles',
      label: t('adminUsers.adminRoles'),
      children: <AdminRolesTab 
        availableRoles={finalAvailableRoles}
        editingRole={editingRole}
        setEditingRole={setEditingRole}
        editingRolePermissions={editingRolePermissions}
        setEditingRolePermissions={setEditingRolePermissions}
        isSubmitting={isSubmitting}
        adminRoleForm={adminRoleForm}
        rolePermissionsForm={rolePermissionsForm}
        handleUpdateRolePermissions={handleUpdateRolePermissions}
        handleCreateRole={handleCreateRole}
        handleUpdateRole={handleUpdateRole}
        refetchRoles={refetchRoles}
      />
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('adminUsers.title')}</Title>
          <Text type="secondary">{t('adminUsers.subtitle')}</Text>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />

      {/* Create/Edit Basic Info Modal */}
      <AdminUserModal
        visible={showCreateModal || !!editingAdmin}
        admin={editingAdmin}
        isSubmitting={isSubmitting}
        form={form}
        doctors={doctors}
        loadingDoctors={loadingDoctors}
        clinics={clinics}
        loadingClinics={loadingClinics}
        availableRoles={finalAvailableRoles}
        loadingRoles={loadingAdminRoles}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingAdmin(null);
          form.resetFields();
        }}
        onSubmit={editingAdmin 
          ? (values) => handleUpdateAdmin(editingAdmin.id, values)
          : handleCreateAdmin
        }
      />

      {/* Edit Permissions Modal */}
      {/* <AdminPermissionsModal
        visible={!!editingAdminPermissions}
        admin={editingAdminPermissions}
        isSubmitting={isSubmitting}
        form={permissionsForm}
        onCancel={() => {
          setEditingAdminPermissions(null);
          permissionsForm.resetFields();
        }}
        onSubmit={(values) => editingAdminPermissions && handleUpdateAdminPermissions(editingAdminPermissions.id, values)}
      /> */}

      {/* Edit Role Modal */}
      {/* <AdminRoleModal
        visible={!!editingAdminRole}
        admin={editingAdminRole}
        isSubmitting={isSubmitting}
        form={roleForm}
        availableRoles={finalAvailableRoles}
        onCancel={() => {
          setEditingAdminRole(null);
          roleForm.resetFields();
        }}
        onSubmit={(values) => editingAdminRole && handleUpdateAdminRole(editingAdminRole.id, values)}
      /> */}

      {/* Create/Edit Role Modal */}
      <RoleModal
        visible={!!editingRole}
        role={editingRole}
        isSubmitting={isSubmitting}
        form={adminRoleForm}
        onCancel={() => {
          setEditingRole(null);
          adminRoleForm.resetFields();
        }}
        onSubmit={editingRole?.id 
          ? (values) => handleUpdateRole(editingRole.id, values)
          : handleCreateRole
        }
      />

      {/* Password Reset Modal */}
      <AdminUserPasswordResetModal
        visible={!!resettingPasswordAdmin}
        admin={resettingPasswordAdmin}
        isSubmitting={isSubmitting}
        form={form}
        onCancel={() => {
          setResettingPasswordAdmin(null);
          form.resetFields();
        }}
        onSubmit={handleResetPassword}
      />
    </div>
  );
};





export default AdminUsers; 